import React from 'react';
import styled from 'styled-components';

import {Row, Col } from 'reactstrap';

import { VerticalNumberAndLabel, ChargeDelayControl, IndividualTreatmentVisualisationBurst, MagstimColor, CurrentLang, CurrentTheme, isPDA } from "common";
import TabBase from 'common/controls/TabBase';

import * as ControlConstants from 'common/controls/Magstim.Control.Constants'
import * as Logging from 'common/utils/Logging'
import * as StringUtils from 'common/utils/StringUtils.js'


class TabBurst extends TabBase {

	constructor() {
		super();

		this.state = {
			inputGroupWidth: isPDA() ?  '100px' : '150px',
			leftColumOffset: isPDA() ?  { size: 10, offset: 0 } : { size: 8, offset: 1 },
			rightColumOffset: isPDA() ? { size: 2, offset: 0 } : { size: 3, offset: 0 } 
		}

		this._elemBurstsPerSecond = React.createRef();
		this._elemPulsesPerBurst = React.createRef();
		this._elemBurstsPerCycle = React.createRef();
		this._elemCycleTime = React.createRef();
		this._elemTotalCycles = React.createRef();

		this._burstsPerSecond = ControlConstants.UI_BURSTS_PER_SEC.default;
		this._pulsesPerSecond = ControlConstants.UI_BURST_PULSES_PER_SEC.default;
		this._pulsesPerBurst = ControlConstants.UI_PULSES_PER_BURST.default;
		this._burstsPerCycle = ControlConstants.UI_BURST_PER_CYCLE.default;
		this._cycleTime = ControlConstants.UI_BURST_CYCLE_TIME.default;
		this._adjustedPowerOutput = ControlConstants.UI_BURST_PC_MT.default;
		this._chargeDelay = ControlConstants.UI_BURST_CHARGE_DELAY.default;
		this._totalCycles = ControlConstants.UI_BURST_TOTAL_CYCLES.default;
	}

	values() {
		var retVal = super.values();
		retVal.burstsPerSecond = this._burstsPerSecond;
		retVal.pulsesPerBurst = this._pulsesPerBurst;
		retVal.burstsPerCycle = this._burstsPerCycle;
		retVal.totalCycles = this._totalCycles;
		retVal.cycleTime = this._cycleTime
		return retVal;
	}

	setValues(values) {

		//console.log('setValues', values);
		
		super.setValues(values);

		if (undefined !== values.burstsPerSecondHighlight) { this._elemBurstsPerSecond.current.setHighlight(values.burstsPerSecondHighlight); }
		if (undefined !== values.pulsesPerBurstHighlight) { this._elemPulsesPerBurst.current.setHighlight(values.pulsesPerBurstHighlight); }
		if (undefined !== values.burstsPerCycleHighlight) { this._elemBurstsPerCycle.current.setHighlight(values.burstsPerCycleHighlight); }
		if (undefined !== values.totalCyclesHighlight) { this._elemTotalCycles.current.setHighlight(values.totalCyclesHighlight); }
		if (undefined !== values.cycleTimeHighlight) { this._elemCycleTime.current.setHighlight(values.cycleTimeHighlight); }

		if (undefined !== values.burstsPerSecond) { this._elemBurstsPerSecond.current.setValue(values.burstsPerSecond); this.setBurstsPerSecond(values.burstsPerSecond); }
		if (undefined !== values.pulsesPerBurst) { this._elemPulsesPerBurst.current.setValue(values.pulsesPerBurst); this.setPulsesPerBurst(values.pulsesPerBurst); }
		if (undefined !== values.burstsPerCycle) { this._elemBurstsPerCycle.current.setValue(values.burstsPerCycle); this.setBurstsPerCycle(values.burstsPerCycle); }
		if (undefined !== values.totalCycles) { this._elemTotalCycles.current.setValue(values.totalCycles); this.setTotalCycles(values.totalCycles); }
		if (undefined !== values.cycleTime) { this._elemCycleTime.current.setValue(values.cycleTime); this.setCycleTime(values.cycleTime); }
		
		this.updateTotalDuration();
		this.updateTotalPulses();
		this.updateBurstDuration();

		// Clear after load
		this.setNonFormikInputsTouched(false);
	}

	setBurstsPerCycle(value) {
		//Logging.log('setBurstsPerCycle ' + this._burstsPerCycle + ' --> ' + value);
		this._elemVisualisation.current.setBurstsPerCycle(value);
		this._burstsPerCycle = value;
	}
	onChangeBurstsPerCycle = (value) => {
		const prevValue = this._burstsPerCycle;

		this.setBurstsPerCycle(value);
		this.updateTotalPulses(); // onChangeBurstsPerCycle
		this.updateTotalDuration();

		if (this.calculateTotalPulses() > ControlConstants.TOTAL_PULSES) {
			console.log(`${this.calculateTotalPulses()} pulses exceeds max (${ControlConstants.TOTAL_PULSES}) resetting BurstsPerCycle back to ${prevValue} from ${value}`);
			this._elemBurstsPerCycle.current.setValue(prevValue);
			this.onChangeBurstsPerCycle(prevValue);
		}

		this.updateCycleTime();
	}

	updatePulsesPerSecondMin() {
		//console.log(`updatePulsesPerSecondMin _elemBurstsPerSecond=${this._elemBurstsPerSecond.current.getValue()} _elemPulsesPerBurst=${this._elemPulsesPerBurst.current.getValue()}`);
		const minPulsesPerSecond = this._elemBurstsPerSecond.current.getValue() * this._elemPulsesPerBurst.current.getValue();
		this._elemPulsesPerSecond.current.setMin(minPulsesPerSecond);
	}

	setBurstsPerSecond(value) {
		//Logging.log('setBurstsPerSecond ' + this._burstsPerSecond + ' --> ' + value);
		this._elemVisualisation.current.setBurstsPerSecond(value);
		this._burstsPerSecond = value;
	}
	onChangeBurstsPerSecond = (value) => {
		this.setBurstsPerSecond(value);
		this.updateTotalDuration();
		this.updateBurstDuration();
		this.updatePulsesPerSecondMin();

		this.updateCycleTime();
	}

	updateCycleTime() {
		const cycletime_calc_min = this._burstsPerCycle / this._burstsPerSecond;
		const cycletime_min = Number(this._elemCycleTime.current.getMin());
		const cycletime_value = this._elemCycleTime.current.getValueAsNumber();
		console.log(`TabBurst.updateCycleTime | BurstsPerSecond.=${this._burstsPerSecond} BurstsPerCycle=${this._burstsPerCycle} cycletime_calc_min=${cycletime_calc_min} cycletime_min=${cycletime_min} cycletime_value=${cycletime_value}`);
		
		if (cycletime_calc_min < ControlConstants.UI_BURST_CYCLE_TIME.min) {
			// calculated cycletime is less than the specified min, so set it back to that...
			this._elemCycleTime.current.setMin(ControlConstants.UI_BURST_CYCLE_TIME.min);
		}
		else { //if (cycletime_calc_min > cycletime_min) {
			this._elemCycleTime.current.setMin(cycletime_calc_min);
		}

		if (cycletime_calc_min > cycletime_value) {
			this._elemCycleTime.current.setValue(cycletime_calc_min);
			this.onChangeCycleTime(cycletime_calc_min);
		}

	}

	setPulsesPerBurst(value) {
		//Logging.log('setPulsesPerBurst ' + this._pulsesPerBurst + ' --> ' + value);
		this._elemVisualisation.current.setPulsesPerBurst(value);
		this._pulsesPerBurst = value;

		this.setNonFormikInputsTouched(true);
	}
	onChangePulsesPerBurst = (value) => {
		const prevValue = this._pulsesPerBurst;

		this.setPulsesPerBurst(value);
		this.updateTotalPulses(); // onChangePulsesPerBurst
		this.updateBurstDuration();
		this.updatePulsesPerSecondMin();

		if (this.calculateTotalPulses() > ControlConstants.TOTAL_PULSES) {
			console.log(`${this.calculateTotalPulses()} pulses exceeds max (${ControlConstants.TOTAL_PULSES}) resetting PulsesPerBurst back to ${prevValue} from ${value}`);
			this._elemPulsesPerBurst.current.setValue(prevValue);
			this.onChangePulsesPerBurst(prevValue);
		}
	}

	setPulsesPerSecond(value) {
		//Logging.log('setPulsesPerSecond ' + this._pulsesPerSecond + ' --> ' + value);
		this._elemVisualisation.current.setPulsesPerSecond(value);
		this._pulsesPerSecond = value;

		this.setNonFormikInputsTouched(true);
	}
	onChangePulsesPerSecond = (value) => {
		this.setPulsesPerSecond(value);
		this.updateBurstDuration();
	}

	setCycleTime(value) {
		// console.log(`TabBurst.setCycleTime value=${value}`);
		this._cycleTime = value;
		this._elemVisualisation.current.setCycleTime(value);

		this.setNonFormikInputsTouched(true);
	}
	onChangeCycleTime = (value) => {
		// console.log(`TabBurst.onChangeCycleTime value=${value}`);
		this.setCycleTime(value);
		this.updateTotalDuration();
	}

	setTotalCycles(value) {
		this._totalCycles = value;

		this.setNonFormikInputsTouched(true);
	}
	onChangeTotalCycles = (value) => {
		const prevValue = this._totalCycles;

		this.setTotalCycles(value);
		this.updateTotalPulses(); // onChangeTotalCycles
		this.updateTotalDuration();

		if (this.calculateTotalPulses() > ControlConstants.TOTAL_PULSES) {
			console.log(`${this.calculateTotalPulses()} pulses exceeds max (${ControlConstants.TOTAL_PULSES}) resetting TotalCycles back to ${prevValue} from ${value}`);
			this._elemTotalCycles.current.setValue(prevValue);
			this.onChangeTotalCycles(prevValue);
		}
	}

	// calculateOnTime() {
	// 	return Number(this._burstsPerCycle) / Number(this._burstsPerSecond);
	// }

	// calculateOffTime() {
	// 	return Number(this._cycleTime) - this.calculateOnTime();
	// }

	calculateTotalDuration() {
		return Number(this._cycleTime) * Number(this._totalCycles);
	}

	calculateTotalPulses() {
		return Number(this._burstsPerCycle) * Number(this._pulsesPerBurst) * Number(this._totalCycles);
	}

	calculatePulsesPerSecondStep(value) {
		return ControlConstants.UI_BURST_PULSES_PER_SEC.step;
	}

	updateBurstDuration() {
		var burstDuration = this._pulsesPerBurst / this._pulsesPerSecond;
		var burstAllowedDuration = 1 / this._burstsPerSecond;
		// console.log(`burstDuration=${burstDuration} burstAllowedDuration=${burstAllowedDuration} _pulsesPerBurst=${this._pulsesPerBurst} _pulsesPerSecond=${this._pulsesPerSecond} _burstsPerSecond=${this._burstsPerSecond}`);
		if (burstDuration > burstAllowedDuration) {
			Logging.log('updateBurstDuration : ERROR STATE');
		}
	}

	componentDidMount() {
		this.updatePulsesPerSecondMin();
	}
	
	render() {
		
		//console.log(`TabBurst.render`, this.props, this.state, this);
		

		if (!this.props.displayMT) {
			this._powerOutput = null;
		}
		return (
			<Wrapper className="col" inputGroupWidth={this.state.inputGroupWidth}>
				<Row>
					<Col md={this.state.leftColumOffset} className="border-right">

						<Row className="ValueEntryRow">
							<VerticalNumberAndLabel id='idPercentageOfMT' ref={this._elemAdjustedPowerOutput} onChange={this.onChangeAdjustedPowerOutput} text={CurrentLang().view_individual_treatment_burst_percentage_of_rmt}
								min={ControlConstants.UI_BURST_PC_MT.min}
								max={ControlConstants.UI_BURST_PC_MT.max}
								step={ControlConstants.UI_BURST_PC_MT.step}
								defaultValue={this._adjustedPowerOutput}
								format={StringUtils.formatPercent}
								size={CurrentTheme().ControlSize}
							/>
							<VerticalNumberAndLabel id='idBurstsPerSecond' ref={this._elemBurstsPerSecond} onChange={this.onChangeBurstsPerSecond} text={CurrentLang().view_individual_treatment_burst_bursts_per_second}
								min={ControlConstants.UI_BURSTS_PER_SEC.min}
								max={ControlConstants.UI_BURSTS_PER_SEC.max}
								step={ControlConstants.UI_BURSTS_PER_SEC.step}
								defaultValue={this._burstsPerSecond}
								size={CurrentTheme().ControlSize}
							/>
							<VerticalNumberAndLabel id='idPulsesPerSecond' ref={this._elemPulsesPerSecond} onChange={this.onChangePulsesPerSecond} text={CurrentLang().view_individual_treatment_burst_pulses_per_second}
								min={ControlConstants.UI_BURST_PULSES_PER_SEC.min}
								max={ControlConstants.UI_BURST_PULSES_PER_SEC.max}
								defaultValue={this._pulsesPerSecond}
								step={this.calculatePulsesPerSecondStep(this._pulsesPerSecond)}
								size={CurrentTheme().ControlSize}
							/>
							<VerticalNumberAndLabel id='idPulsesPerBurst' ref={this._elemPulsesPerBurst} onChange={this.onChangePulsesPerBurst} text={CurrentLang().view_individual_treatment_burst_pulses_per_burst}
								min={ControlConstants.UI_PULSES_PER_BURST.min}
								max={ControlConstants.UI_PULSES_PER_BURST.max}
								step={ControlConstants.UI_PULSES_PER_BURST.step}
								defaultValue={this._pulsesPerBurst}
								size={CurrentTheme().ControlSize}
							/>
							<VerticalNumberAndLabel id='idBurstsPerCycle' ref={this._elemBurstsPerCycle} onChange={this.onChangeBurstsPerCycle} text={CurrentLang().view_individual_treatment_burst_bursts_per_cycle}
								min={ControlConstants.UI_BURST_PER_CYCLE.min}
								max={ControlConstants.UI_BURST_PER_CYCLE.max}
								step={ControlConstants.UI_BURST_PER_CYCLE.step}
								defaultValue={this._burstsPerCycle}
								size={CurrentTheme().ControlSize}
							/>
							<VerticalNumberAndLabel id='idCycleTime' ref={this._elemCycleTime} onChange={this.onChangeCycleTime} text={CurrentLang().view_individual_treatment_burst_cycle_time}
								min={ControlConstants.UI_BURST_CYCLE_TIME.min}
								max={ControlConstants.UI_BURST_CYCLE_TIME.max}
								defaultValue={this._cycleTime}
								step={ControlConstants.UI_BURST_CYCLE_TIME.step}
								format={StringUtils.formatSeconds}
								size={CurrentTheme().ControlSize}
							/>
						</Row>

						<Row className="delayRow">
							<Col md={3} className="ml-2 mr-2">
								<ChargeDelayControl ref={this._elemChargeDelay}
									onChangeDuration={this.onChangeChargeDelay}
									onChangeIsEnabled={this.onChangeChargeDelayIsEnabled}
									defaultValue={this._chargeDelay}
									min={ControlConstants.UI_BURST_CHARGE_DELAY.min}
									max={ControlConstants.UI_BURST_CHARGE_DELAY.max}
									step={ControlConstants.UI_BURST_CHARGE_DELAY.step}
									isEnabled={false}
									id="idChargeDelayControlBurst"
								/>
							</Col>
							<Col className="data-chart">
								<IndividualTreatmentVisualisationBurst
									ref={this._elemVisualisation}
									AdjustedPowerOutput={this._adjustedPowerOutput}
									PowerOutput={this._powerOutput}
									ChargeDelay={null} // this._chargeDelay but as it's disabled by default
									BurstsPerCycle={this._burstsPerCycle}
									BurstsPerSecond={this._burstsPerSecond}
									PulsesPerBurst={this._pulsesPerBurst}
									PulsesPerSecond={this._pulsesPerSecond}
									CycleTime={undefined}
								/>
							</Col>
						</Row>

					</Col>

					<Col md={this.state.rightColumOffset} className="totalValues">
						<Row className="ValueEntryRow">
							<VerticalNumberAndLabel ref={this._elemTotalCycles} id='idTotalCycles' onChange={this.onChangeTotalCycles} text={CurrentLang().view_individual_treatment_burst_total_cycles}
								min={ControlConstants.UI_BURST_TOTAL_CYCLES.min}
								max={ControlConstants.UI_BURST_TOTAL_CYCLES.max}
								defaultValue={this._totalCycles}
								step={ControlConstants.UI_BURST_TOTAL_CYCLES.step}
								size={CurrentTheme().ControlSize}
							/>
						</Row>
						<Row className="ValueEntryRow">
							<VerticalNumberAndLabel id='idTotalPulses' ref={this._elemTotalPulses} defaultValue={this.calculateTotalPulses()} text={CurrentLang().view_individual_treatment_burst_total_pulses} readonly={true} size={CurrentTheme().ControlSize} />
						</Row>
						<Row className="ValueEntryRow">
							<VerticalNumberAndLabel id='idTotalDurationBurst' ref={this._elemTotalDuration} defaultValue={this.calculateTotalDuration()} text={CurrentLang().view_individual_treatment_burst_total_duration} readonly={true} format={StringUtils.formatMinuteAndSeconds} size={CurrentTheme().ControlSize} />
						</Row>
					</Col>

				</Row>


			</Wrapper>
		);
	}
}

export default TabBurst;

const Wrapper = styled.section`
	margin: 0;
	padding: 0;
	background: ${ MagstimColor.MagstimGroupBackground};
	color: ${ MagstimColor.MagstimWhite};

	.ValueEntryRow {
		margin: 0
		padding-top: 30px;
		padding-bottom: 30px;

		input {
			color: ${ MagstimColor.MagstimWhite};
			font-weight: bold;
			width:  ${props => props.inputGroupWidth};
			margin-left: auto;
			margin-right: auto;
			text-align: center

			&:focus {
				color: ${ MagstimColor.MagstimPrimaryBlue};
			}
		}

		.col {
			vertical-align: bottom;
			align-self: flex-end;
		}
	}
	.totalValues .ValueEntryRow {
		padding-bottom: 25px;
	}
	.delayRow {
		padding-bottom: 30px;
	}
	.data-chart {
		padding: 20px 30px 20px 0 ;
	}
`;